import axios from 'axios'
import { gerenciadorUrl } from '@/config/gerenciadorConfig'
import { montarTensoes, tensaoPadrao, aplicarTensao } from '@/services/tensao'

let CACHE = {}

// const CACHE_DURATION = 1000 * 60 * 5 // 5 min
const CACHE_DURATION = 0
const DEFAULT_TIMEOUT = 60000

// O antigo backend Railway/Django foi definitivamente desativado. O
// Gerenciador de Produtos MX3 passou a ser a ÚNICA fonte de dados deste
// e-commerce - não há mais nenhuma chamada a railway.app em nenhum
// ambiente (produção ou desenvolvimento). A URL do Gerenciador (incluindo
// o domínio padrão de fallback) é resolvida de forma centralizada em
// @/config/gerenciadorConfig.js - ver esse arquivo para mudar de domínio.

// Mantido apenas como baseURL "neutra" da instância axios (ver httpService).
// Como toda chamada deste arquivo usa URLs absolutas, este valor nunca é
// efetivamente utilizado para montar requisições.
let host = gerenciadorUrl()


export function getAccessToken() {
    return localStorage.getItem('accessToken')
}


function getHeader() {
    let header = { 'Content-Type': 'application/json' }
    const token = getAccessToken()
    if (token) {
        header['Authorization'] = `Bearer ${token}`
        // Plano B: alguns hosts/proxies removem o header "Authorization"
        // padrão antes de repassar a requisição ao PHP (mesmo com as regras
        // do .htaccess). Enviar o token também num header customizado
        // aumenta a chance de o backend conseguir lê-lo em qualquer
        // ambiente - o backend (ecommerceObterAuthorizationHeader) já
        // aceita este header como alternativa.
        header['X-Access-Token'] = token
    }
    return header
}


// Indica se a requisição é para o Gerenciador de Produtos (endpoints públicos
// de leitura, cross-origin) e NÃO para a API local do e-commerce (/api/*.php),
// mesmo quando os dois estão no mesmo domínio.
function isGerenciadorRequest(request) {
    const rawUrl = request.url || ''
    try {
        const base = new URL(gerenciadorUrl(), window.location.href)
        // URL relativa é resolvida pelo axios contra o baseURL (Gerenciador).
        const target = new URL(rawUrl, request.baseURL || window.location.href)
        if (target.hostname !== base.hostname) return false
        const basePath = base.pathname.replace(/\/+$/, '')
        if (basePath) {
            return target.pathname === basePath || target.pathname.startsWith(basePath + '/')
        }
        // Gerenciador na raiz de outro domínio: só conta se for outra origem.
        return target.origin !== window.location.origin
    } catch (e) {
        return false
    }
}

const onRequest = (request) => {
    // console.log('REQUISIÇÃO ENVIADA:', request);

    // Se for uma requisição para o Gerenciador de Produtos (domínio resolvido dinamicamente),
    // removemos os headers de autenticação (Authorization e X-Access-Token).
    // Esses endpoints são públicos, somente leitura, e o Gerenciador não
    // reconhece/precisa do token de sessão do cliente do e-commerce -
    // nenhum dos dois hosts libera o header customizado "X-Access-Token" no
    // Access-Control-Allow-Headers do CORS, então mandar esse header em
    // QUALQUER requisição cross-origin para eles faz o preflight falhar com
    // "Request header field x-access-token is not allowed" - derrubando com
    // "Network Error" TODAS as chamadas cross-origin (categorias, banners,
    // produtos, etc.), mesmo sem nenhuma relação com login. Por isso a
    // checagem cobre qualquer método, não só GET: o preflight é disparado
    // pelo header customizado independentemente do verbo HTTP usado.
    //
    // IMPORTANTE: a checagem deve olhar apenas para a URL de fato chamada
    // (`request.url`), nunca para `request.baseURL`. A instância axios criada
    // em httpService() é sempre configurada com `baseURL: host` (o
    // Gerenciador), mesmo quando a chamada é feita para uma URL absoluta
    // local (ex.: window.location.origin + '/api/addon.php') - nesse caso o
    // axios ignora o baseURL ao montar a requisição, mas `request.baseURL`
    // continua presente no objeto da requisição só como metadado do padrão
    // da instância. Usar `baseURL.includes(...)` faria essa condição ser
    // sempre verdadeira, removendo o Authorization de TODA requisição -
    // inclusive chamadas autenticadas locais como /api/addon.php,
    // /api/order_list.php e /api/cliente_password.php. Era por isso que,
    // após o login, getAddon() sempre recebia 401 (sem o Bearer token), o
    // que disparava a limpeza total do localStorage (apagando o token
    // recém-salvo) e o redirecionamento para "/" - dando a impressão de que
    // o login "não colava", quando na verdade ele funcionava e era desfeito
    // em seguida.
    //
    // Nota: o antigo backend Railway foi definitivamente desativado e todas
    // as chamadas a ele foram removidas deste arquivo - não existe mais
    // nenhuma verificação de "railway.app" aqui.
    // v1910_137: o Gerenciador (/admin) e o e-commerce ficam no MESMO domínio
    // (www.importmaisbrasil.com.br). A checagem antiga comparava só o nome do
    // host (url.includes(host)), então TODA chamada local - inclusive
    // /api/addon.php logo após o login - era tratada como "do Gerenciador" e
    // perdia o token: addon.php devolvia 401, a sessão era limpa e o usuário
    // voltava para "/" sem conseguir logar. Agora só perde o token a chamada
    // que realmente aponta para o Gerenciador (host + pasta, ex.: /admin).
    if (isGerenciadorRequest(request)) {
        if (request.headers.Authorization) {
            delete request.headers.Authorization;
        }
        if (request.headers['X-Access-Token']) {
            delete request.headers['X-Access-Token'];
        }
    }

    return request
}


// Remove os dados de sessão/autenticação do cliente (token, dados do addon,
// flag de admin) e o carrinho exibido localmente - mesmo critério usado no
// logout manual (AccountMenu.vue). O carrinho não é perdido: ele continua
// salvo no servidor vinculado à conta (ver App.vue -> /api/cart_save.php) e
// é restaurado automaticamente no próximo login com a mesma conta.
const clearAuthStorage = () => {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('addon')
    localStorage.removeItem('isAdmin')
    localStorage.removeItem('itensCarrinho')
    localStorage.removeItem('carrinhoQuantidades')
    localStorage.removeItem('carrinhoTensoes')
    localStorage.removeItem('carrinhoContaRestaurado')
}

const onRequestSuccess = (response) => {
    // console.log('REQUISIÇÃO RECEBIDA COM SUCESSO:', response);
    return response
}


const onRequestError = (error) => {
    console.log('REQUISIÇÃO RECEBIDA COM ERRO:', error);

    if (error.response) {
        // Server responded with a status other than 2xx
        console.error('HTTP Error:', error.response.status, error.response.data);
        if ([401, 403].includes(error.response.status)) {
            // Handle unauthorized access, e.g., redirect to login
            clearAuthStorage()
            window.location.href = '/';
        }
    } else if (error.request) {
        // Request was made but no response received
        console.error('No response received:', error.request);
    } else {
        // Something else caused the error
        console.error('Error setting up request:', error.message);
    }
    return Promise.reject(error);
}


export function httpService() {
    const httpService = axios.create({
        baseURL: host,
        timeout: DEFAULT_TIMEOUT,
        headers: getHeader()
    })

    httpService.interceptors.response.use(onRequestSuccess, onRequestError)
    httpService.interceptors.request.use(onRequest)

    return httpService
}


export function get(url, data) {
    return httpService().get(url, { params: data })
}


export function post(url, data) {
    return httpService().post(url, data)
}


export function put(url, data) {
    return httpService().put(url, data)
}

export function destroy(url, data) {
    return new Promise((resolve, reject) => {
        httpService().delete(url, data)
            .then(() => {
                resolve()
                alert('Removido com sucesso')
            })
            .catch(reject)
    })
}


async function fetchData(key, url, cache_ttl) {
    const response = await httpService().get(url)
    return response
}

async function fetchDataCache(key, url, minutes) {
    let cache_ttl = minutes * 60 * 1000
    const cachedData = localStorage.getItem(key);
    const currentTime = new Date().getTime();

    // Verifica se existe cache e se não expirou
    if (cachedData) {
        const parsedData = JSON.parse(cachedData);

        // Se o cache não expirou, retorna o dado em cache
        if (currentTime - parsedData.timestamp < cache_ttl) {
            return parsedData.data;
        } else {
            // Caso tenha expirado, remove o cache antigo
            localStorage.removeItem(key);
        }
    }

    // Faz a requisição e espera o resultado
    const response = await httpService().get(url);

    // Armazena a resposta no cache com o timestamp de criação
    const dataToCache = {
        data: response,
        timestamp: currentTime,
    };

    // Salva o novo cache
    // localStorage.setItem(key, JSON.stringify(dataToCache));

    return response;
}



function toNumber(value) {
    if (value === null || value === undefined || value === '') return 0
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0
    if (typeof value === 'string') {
        const normalized = value
            .replace(/[^0-9,.-]/g, '')
            .replace(/\.(?=\d{3}(\D|$))/g, '')
            .replace(',', '.')
        const parsed = parseFloat(normalized)
        return Number.isFinite(parsed) ? parsed : 0
    }
    const parsed = parseFloat(value)
    return Number.isFinite(parsed) ? parsed : 0
}

function getCompleteProductPrice(product = {}) {
    const hardwarePrice = toNumber(product.calculated_hardware_price)
    const softwarePrice = toNumber(product.calculated_software_price)

    if (hardwarePrice > 0 || softwarePrice > 0) {
        return Number((hardwarePrice + softwarePrice).toFixed(2))
    }

    return toNumber(product.price)
}

export default {
    host,
    httpService,

    get,
    post,
    put,
    destroy,

    accessToken: getAccessToken(),

    login(credentials = { username: '', password: '' }) {
        // Login de clientes agora é feito localmente (mesmo servidor do
        // e-commerce), que por sua vez consulta o Gerenciador de Produtos -
        // substitui o antigo backend Railway.
        return post(window.location.origin + '/api/login.php', credentials)
    },

    logout() {
        clearAuthStorage()
        window.location.href = '/login'
    },

    registrarCliente(dadosCliente) {
        return post(window.location.origin + '/api/register.php', dadosCliente)
    },

    // `destino`: caminho interno (ex.: "/product12?aba=avaliacoes") para onde levar o cliente
    // depois do login; qualquer valor que não seja um caminho interno vira "/".
    getAddon(destino = '/') {
        const esInterno = typeof destino === 'string' && destino.charAt(0) === '/' && destino.charAt(1) !== '/' && destino.charAt(1) !== '\\' && !/^\/(login|register)([\/?]|$)/.test(destino)
        const irPara = esInterno ? destino : '/'
        return httpService().get(window.location.origin + '/api/addon.php')
            .then(res => {
                localStorage.setItem('addon', JSON.stringify(res.data))
                if (res?.data?.isAdmin) {
                    localStorage.setItem('isAdmin', true)
                }
                window.location.href = irPara
                return res;
            })
            .catch(err => {
                console.error('Erro ao buscar addon:', err)
                window.location.href = '/'
                return Promise.reject(err)
            })
    },

    getOpcoesFrete(cep = '') {
        // Cálculo de frete real hoje é feito diretamente em Cart.vue via
        // /api/frete_melhorenvio.php; este método é mantido apenas por
        // compatibilidade com eventuais chamadas antigas.
        return post(window.location.origin + '/api/frete_melhorenvio.php', { 'zip': cep, 'products': [] })
    },

    // --- Produtos ---
    async getProdutos(params = { search: '', categoria: '', sku: '', nome: '', subcategoria: '', tag: '' }) {
        console.log('get produtos', params);

        // Fonte única de produtos: o Gerenciador (não há mais fallback para o
        // antigo backend Railway, que foi desativado).
        const url = `${gerenciadorUrl()}/api/dados_api.php`;

        try {
            const res = await fetchData('produtos_all', url, 5);
            let products = res.data.products || [];

            // Normalização dos campos para compatibilidade com o e-commerce.
            // O preço exibido/vendido deve representar o produto completo:
            // Preço (HW) + Preço (SW). Em alguns registros, `price` ainda contém
            // somente o valor de hardware; por isso usamos os campos calculados
            // vindos do gerenciador como fonte prioritária e segura.
            products = products.map(p => {
                const precoHardware = toNumber(p.calculated_hardware_price)
                const precoSoftware = toNumber(p.calculated_software_price)
                const precoCompleto = getCompleteProductPrice(p)

                const produtoNormalizado = {
                    ...p,
                    id: p.id,
                    nome: p.name,
                    descricao: p.description,
                    price_original: p.price,
                    price: precoCompleto,
                    preco: precoCompleto,
                    preco_hardware: precoHardware,
                    preco_software: precoSoftware,
                    estoque: p.stock,
                    categoria: p.category,
                    subcategoria: p.subcategory,
                    filtroproduto: p.filter,
                    imagens: p.images || [],
                    characteristics: p.applications || p.characteristics || [],
                    technicalSpecs: p.technical_specs || p.technicalSpecs || {},
                    // Metadados comerciais opcionais: os produtos antigos continuam válidos.
                    aplicacao: p.application || p.aplicacao || p.applications || '',
                    compatibilidade: p.compatibility || p.compatibilidade || '',
                    itens_inclusos: p.included_items || p.itens_inclusos || [],
                    itens_recomendados: p.recommended_items || p.itens_recomendados || [],
                    prazo_entrega: p.delivery_time || p.prazo_entrega || '',
                    garantia: p.warranty || p.garantia || '',
                    disponibilidade: p.availability || (Number(p.stock) > 0 ? 'Pronta entrega' : 'Sob consulta'),
                    // Peso/dimensões usados no cálculo de frete (Cart.vue ->
                    // frete_melhorenvio.php). O catálogo guarda esses campos
                    // em inglês (weight/height/width/length); sem este alias,
                    // peso/altura/largura/comprimento ficavam sempre
                    // undefined para TODO produto, e o cálculo de frete
                    // sempre usava as dimensões mínimas genéricas em vez do
                    // tamanho/peso real - o que causava respostas inválidas
                    // ou "nenhuma transportadora atende" para produtos
                    // grandes/pesados/de alto valor (ex.: switches), já que
                    // a transportadora recebia um pacote minúsculo declarado
                    // com um valor segurado de dezenas de milhares de reais.
                    peso: toNumber(p.weight),
                    altura: toNumber(p.height),
                    largura: toNumber(p.width),
                    comprimento: toNumber(p.length),
                }

                // Produto com as duas tensões (110V e 220V): continua sendo UM produto; `tensoes`
                // guarda preço/estoque de cada tensão e o objeto já vem com os da tensão padrão
                // (preferência do cliente > região > tensão principal) - ver services/tensao.js.
                produtoNormalizado.tensoes = montarTensoes(p, precoCompleto, precoSoftware)
                return produtoNormalizado.tensoes.length
                    ? aplicarTensao(produtoNormalizado, tensaoPadrao(produtoNormalizado))
                    : produtoNormalizado
            });

            // Filtragem manual (já que a API retorna tudo)
            if (params.categoria) {
                products = products.filter(p => String(p.categoria) === String(params.categoria) || String(p.category) === String(params.categoria));
            }
            if (params.subcategoria) {
                products = products.filter(p => String(p.subcategoria) === String(params.subcategoria) || String(p.subcategory) === String(params.subcategoria));
            }
            if (params.filtroproduto) {
                products = products.filter(p => String(p.filtroproduto) === String(params.filtroproduto) || String(p.filter) === String(params.filtroproduto));
            }
            if (params.nome || params.search) {
                const search = (params.nome || params.search).toLowerCase();
                products = products.filter(p => 
                    p.nome.toLowerCase().includes(search) || 
                    p.descricao.toLowerCase().includes(search)
                );
            }

            return { data: products };
        } catch (error) {
            console.error("Erro ao buscar produtos:", error);
            return { data: [] };
        }
    },

    async getProduto(id) {
        if (!id) return Promise.reject('ID do produto é necessário');
        const res = await this.getProdutos();
        const product = res.data.find(p => String(p.id) === String(id));
        return { data: product };
    },
    // Faixas de desconto por quantidade configuradas na Precificação do Gerenciador
    // ({ above: quantidade, percent: desconto }). Em caso de falha, devolve lista vazia
    // (nenhum desconto é prometido ao cliente).
    async getDescontosQuantidade() {
        try {
            const response = await fetch(`${gerenciadorUrl()}/api/quantity_discounts.php`, { cache: 'no-store' });
            const data = await response.json();
            if (response.ok && data && data.success && Array.isArray(data.tiers)) {
                return data.tiers
                    .map(t => ({ above: parseInt(t.above, 10), percent: parseFloat(t.percent) }))
                    .filter(t => t.above >= 1 && t.percent > 0)
                    .sort((x, y) => x.above - y.above);
            }
        } catch (e) { /* sem desconto em caso de falha */ }
        return [];
    },
    // Velocidade dos carrosséis em movimento contínuo (Personalizar > Modo Edição > clicar na seção).
    // Resolve { chave: { speed } }; em caso de falha devolve {} (usa a velocidade padrão).
    // A consulta é feita uma única vez por carregamento da página.
    getConfigCarrosseis() {
        if (!this._carrosseisPromise) {
            this._carrosseisPromise = fetch(`${gerenciadorUrl()}/api/carousel_settings.php`, { cache: 'no-store' })
                .then((r) => r.json())
                .then((d) => (d && d.success && d.sections && typeof d.sections === 'object' ? d.sections : {}))
                .catch(() => ({}));
        }
        return this._carrosseisPromise;
    },
    // Condições de venda gerais (Pix, parcelas, frete grátis, "mais vendido"...) da Precificação
    // do Gerenciador. Em caso de falha devolve null (a loja mostra só o preço, sem promessas).
    async getCondicoesVenda() {
        try {
            const response = await fetch(`${gerenciadorUrl()}/api/sales_conditions.php`, { cache: 'no-store' });
            const data = await response.json();
            if (response.ok && data && data.success && data.config) {
                return { config: data.config, rankings: data.rankings || {} };
            }
        } catch (e) { /* sem condições em caso de falha */ }
        return null;
    },
    async enviarLeadComercial(payload = {}) {
        const endpoint = `${gerenciadorUrl()}/api/leads.php`;
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (!response.ok || !data.success) throw new Error(data.message || 'Não foi possível enviar a solicitação.');
        return data;
    },

    // --- Imagens do Produto ---
    // Endpoint legado do antigo backend Railway, sem equivalente no
    // Gerenciador. As imagens do produto já vêm no próprio objeto retornado
    // por getProdutos()/getProduto() (campo `imagens`), então este método
    // hoje serve apenas como fallback inofensivo: resolve vazio em vez de
    // tentar contatar um host desativado.
    async getImagemProduto() {
        return { data: [] }
    },

    // --- Destaques ---
    getDestaquesSite(params = { produto: '' }) {
        const query = new URLSearchParams()
        if (params.produto) query.append("produto", params.produto)

        // Destaques/banners vêm sempre do Gerenciador.
        const url = `${gerenciadorUrl()}/api/banners.php?${query.toString()}`;

        return fetchData('destaques_site', url);
    },

    // --- Endpoints legados (antigo backend Railway/Django) ---
    // Sem equivalente no Gerenciador. As características e especificações
    // técnicas do produto já vêm no próprio objeto de getProdutos()/getProduto()
    // (campos `characteristics`/`technicalSpecs`), então estes métodos
    // servem apenas como fallback inofensivo: resolvem vazio em vez de
    // tentar contatar um host desativado.
    async getCaracteristicaProduto() {
        return { data: [] }
    },
    async getInformacaoTecnicaProduto() {
        return { data: [] }
    },
    async getDetalheInformacaoTecnicaProduto() {
        return { data: [] }
    },
    async getConteudoCaixaProduto() {
        return { data: [] }
    },

    // --- Detalhes Ricos do Produto (Descrição Detalhada e FAQ) ---
    async getProductDetails(productId) {
        if (!productId) return Promise.reject('ID do produto é necessário');
        
        // Tenta buscar do gerenciador local primeiro
        // Usamos a ação 'get' mas sem autenticação se for via proxy ou se o endpoint permitir
        // No nosso caso, o product_details_api.php exige login para 'get'. 
        // Vamos usar 'get_all_for_ecommerce' que é público e filtrar aqui, ou ajustar o PHP.
        
        let url = `${gerenciadorUrl()}/product_details_api.php`;
        if (process.env.VUE_APP_ENV === 'dev' || window.location.hostname === 'localhost') {
            url = 'http://localhost/admin/product_details_api.php';
        }
        
        try {
            // Usamos get_all_for_ecommerce para evitar problemas de sessão no frontend do e-commerce
            const response = await fetch(`${url}?action=get_all_for_ecommerce`);
            if (response.ok) {
                const data = await response.json();
                const allDetails = data.productDetails || {};
                return { data: allDetails[String(productId)] || {} };
            }
        } catch (error) {
            console.error('Erro crítico ao buscar detalhes do produto:', error);
        }
        
        return { data: {} };
    },

    // --- categorias ---
    async getCategorias() {
        const url = `${gerenciadorUrl()}/api/dados_api.php`;

        try {
            const res = await fetchData('categorias_all', url, 5);
            const categories = res.data.categories || [];
            
            // Normalização para o formato esperado pelo e-commerce
            const formattedCategories = categories.map(cat => ({
                id: cat,
                nome: cat
            }));

            return { data: formattedCategories };
        } catch (error) {
            console.error("Erro ao buscar categorias:", error);
            return { data: [] };
        }
    },

    async getCategoria(id) {
        const res = await this.getCategorias();
        const category = res.data.find(c => String(c.id) === String(id));
        return { data: category };
    },

    // --- Banners do Carrossel ---
    getBanners() {
        // No cPanel, o Gerenciador fica no domínio/caminho configurado em "Caminhos e URLs" (ver gerenciadorConfig.js).
        const bannerUrl = `${gerenciadorUrl()}/api/banners.php`;
        return fetchData('banners', bannerUrl, 5); // Cache curto de 5 minutos
    },

    // --- Avaliações de produtos ---
    // Gravadas pelo servidor em arquivos JSON (admin/data/ecommerce_avaliacoes.json) e fotos em
    // admin/images/avaliacoes (api/avaliacoes_*.php, api/avaliacao_*.php). Todas as chamadas devolvem
    // { ok, status, data } - nunca lançam erro, para a tela poder mostrar a mensagem do servidor.
    async avaliacoesRequest(arquivo, { method = 'GET', query = null, json = null, formData = null } = {}) {
        const headers = { Accept: 'application/json' }
        const token = getAccessToken()
        if (token) {
            headers['Authorization'] = `Bearer ${token}`
            headers['X-Access-Token'] = token
        }
        let url = window.location.origin + '/api/' + arquivo
        if (query) {
            const qs = Object.keys(query)
                .filter((k) => query[k] !== undefined && query[k] !== null && query[k] !== '')
                .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(query[k]))
                .join('&')
            if (qs) url += '?' + qs
        }
        let body
        if (formData) {
            body = formData
        } else if (json) {
            headers['Content-Type'] = 'application/json'
            body = JSON.stringify(json)
        }
        try {
            const resp = await fetch(url, { method, headers, body })
            let data = {}
            try { data = await resp.json() } catch (e) { data = {} }
            return { ok: resp.ok, status: resp.status, data }
        } catch (e) {
            return { ok: false, status: 0, data: { error: 'Não foi possível conectar ao servidor. Verifique sua internet.' } }
        }
    },
    getAvaliacoes(params = {}) {
        return this.avaliacoesRequest('avaliacoes_list.php', { query: params })
    },
    getResumoAvaliacoes() {
        return this.avaliacoesRequest('avaliacoes_resumo.php').then((r) => (r.ok && r.data && r.data.resumo) || {})
    },
    enviarAvaliacao(formData) {
        return this.avaliacoesRequest('avaliacao_enviar.php', { method: 'POST', formData })
    },
    votarAvaliacao(id, voto) {
        return this.avaliacoesRequest('avaliacao_voto.php', { method: 'POST', json: { id, voto } })
    },
    denunciarAvaliacao(id) {
        return this.avaliacoesRequest('avaliacao_denunciar.php', { method: 'POST', json: { id } })
    },

    // --- Configurações Públicas do Site ---
    // Removido: este método buscava a configuração "ocultar preço para
    // usuários não logados" diretamente do antigo backend Railway/Django
    // (`${host}/configuracoes-publicas/`), uma tela de administração órfã
    // que ficou para trás na migração para o Gerenciador de Produtos e
    // nunca mais recebeu atualizações. A configuração agora é gerenciada
    // na aba "Exibição de Preços" da tela "Configurações" do Gerenciador
    // de Produtos e consumida via services/apiConfig.js
    // (getPublicApiConfig), usado em App.vue -> loadConfiguracoesSite().
}
